import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/lib/data';
import { DEFAULT_CERTIFICATES } from '@/lib/defaults';
import { Reveal } from '@/components/Reveal';
import { SEO, createPersonSchema, createBreadcrumbSchema } from '@/components/SEO';
import { Breadcrumb } from '@/components/Breadcrumb';
import { useSettings } from '@/lib/useSettings';
import { OptimizedImage } from '@/components/OptimizedImage';
import { motion } from 'framer-motion';
import { GraduationCap, Award, BookOpen, Heart, Youtube, Instagram, Facebook } from 'lucide-react';

const certIcons = { Diploma: GraduationCap, Sertifika: Award, Akademik: BookOpen };

export default function AboutPage() {
  const [certificates, setCertificates] = useState(DEFAULT_CERTIFICATES);
  const settings = useSettings();
  const personSchema = createPersonSchema(settings);

  const doctorName = settings.doctor?.name || 'Prof. Dr. Nilüfer Yıldırım';
  const doctorTitle = settings.doctor?.title || 'Fonksiyonel Tıp';
  const doctorImage = settings.doctor?.image;
  const doctorBio = settings.doctor?.bio;
  const clinicApproach = settings.doctor?.clinic_approach;
  const detailBio = settings.doctor?.detail_bio;
  const social = settings.social || {};

  useEffect(() => {
    axios.get(`${API}/certificates`).then(r => setCertificates(r.data)).catch(console.error);
  }, []);

  const socialLinks = [
    { url: social.youtube, icon: Youtube, label: 'YouTube' },
    { url: social.instagram, icon: Instagram, label: 'Instagram' },
    { url: social.facebook, icon: Facebook, label: 'Facebook' },
  ].filter(s => s.url);

  return (
    <div data-testid="about-page">
      <SEO title="Hakkımda" description={`${doctorName} - ${doctorTitle}. Fonksiyonel tıp alanında uzman hekim. Ankara'da kişiye özel sağlık danışmanlığı.`} schema={[personSchema, createBreadcrumbSchema([{ name: 'Ana Sayfa', url: '/' }, { name: 'Hakkımda' }])]} />
      <Breadcrumb items={[{ label: 'Hakkımda', href: '/hakkimizda', current: true }]} />

      {/* Hero Banner */}
      <section className="relative bg-[#1F6E7A] py-14 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#1F6E7A] via-[#1F6E7A]/90 to-[#1F6E7A]/70" />
        {doctorImage && (
          <div className="absolute inset-0 opacity-[0.08]">
            <img src={doctorImage} alt="" className="w-full h-full object-cover" />
          </div>
        )}
        <div className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 text-center">
          <h1 className="font-heading text-[34px] sm:text-[34px] lg:text-[46px] font-bold text-white tracking-[-0.02em] mb-3">
            Hakkımda
          </h1>
          <p className="text-[22px] sm:text-[24px] text-white/80">{doctorName}</p>
        </div>
      </section>

      {/* Main Content: Left sidebar + Right bio */}
      <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 py-12 sm:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-10 lg:gap-14">

          {/* Left Column - Doctor Card */}
          <div className="flex flex-col items-center lg:items-start lg:sticky lg:top-28 lg:self-start">
            {doctorImage && (
              <div className="relative mb-5">
                <div className="absolute -inset-3 rounded-full bg-[#1F6E7A]/10 blur-xl" />
                <OptimizedImage
                  src={doctorImage}
                  alt={doctorName}
                  className="relative w-48 h-48 sm:w-56 sm:h-56 rounded-full object-cover border-4 border-[#1F6E7A]/15 shadow-lg"
                  sizes="224px"
                />
              </div>
            )}
            <h2 className="font-heading text-[24px] sm:text-[22px] font-bold text-[#1F2937] mb-1 text-center lg:text-left">
              {doctorName}
            </h2>
            <p className="text-[14px] text-[#1F6E7A] font-medium mb-4 text-center lg:text-left">{doctorTitle}</p>

            {/* Social Media */}
            {socialLinks.length > 0 && (
              <div className="flex items-center gap-3 mt-1" data-testid="about-social-links">
                {socialLinks.map(({ url, icon: Icon, label }) => (
                  <a
                    key={label}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#1F6E7A]/10 flex items-center justify-center text-[#1F6E7A] hover:bg-[#1F6E7A] hover:text-white transition-all duration-300"
                    title={label}
                    data-testid={`about-social-${label.toLowerCase()}`}
                  >
                    <Icon size={18} />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Right Column - Biography Content */}
          <div className="space-y-16">
            {/* Short Bio */}
            {doctorBio && (
              <Reveal>
                <div data-testid="short-bio-section">
                  <p className="text-[22px] text-[#374151] leading-relaxed whitespace-pre-line pb-16">
                    {doctorBio}
                  </p>
                </div>
              </Reveal>
            )}

            {/* Detail Bio */}
            {detailBio && (
              <Reveal>
                <div className="pt-6 border-t border-[#E7E5E4]" data-testid="detail-bio-section">
                  <div
                    className="prose prose-lg max-w-none text-[#374151] leading-relaxed [&_p]:mb-4 [&_p]:text-[14px] [&_p:empty]:mb-4 [&_p:empty]:h-4 [&_br]:block [&_br]:mb-4 [&_strong]:text-[#1F2937] [&_li]:text-[14px] [&_h2]:text-[30px] [&_h3]:text-[24px]"
                    data-testid="detail-bio-content"
                    dangerouslySetInnerHTML={{ __html: detailBio }}
                  />
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* Clinic Approach */}
      {clinicApproach && (
        <Reveal>
          <section className="bg-[#F5F3EF] py-14 sm:py-16">
            <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-[#1F6E7A]/10 flex items-center justify-center">
                  <Heart size={20} className="text-[#1F6E7A]" />
                </div>
                <h2 className="font-heading text-[30px] font-semibold">Klinik Yaklaşımımız</h2>
              </div>
              <div className="bg-white rounded-[28px] border border-[#E7E5E4] p-6 sm:p-8">
                <p className="text-[14px] text-[#5F6B76] leading-relaxed whitespace-pre-line">{clinicApproach}</p>
              </div>
            </div>
          </section>
        </Reveal>
      )}

      {/* Certificates Timeline */}
      {certificates.length > 0 && (
        <Reveal>
          <section className="py-14 sm:py-16">
            <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-10">
              <h2 className="font-heading text-[30px] font-semibold mb-8 text-center">Diploma, Sertifika & Üyelikler</h2>
              <div className="relative">
                <div className="absolute left-[18px] sm:left-[22px] top-0 bottom-0 w-px bg-[#E7E5E4]" />
                {[...certificates].sort((a, b) => (parseInt(b.year) || 0) - (parseInt(a.year) || 0)).map((c, i) => {
                  const IconComp = certIcons[c.type] || Award;
                  return (
                    <motion.div
                      key={c.id}
                      className="relative flex items-start gap-4 sm:gap-5 mb-6 last:mb-0 pl-1"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="relative z-10 w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-white border border-[#E7E5E4] flex items-center justify-center shrink-0 shadow-sm">
                        <IconComp size={16} className="text-[#D8C7A6]" />
                      </div>
                      <div className="flex-1 pt-1">
                        <p className="text-[14px] font-semibold text-[#D8C7A6] mb-0.5">{c.year}</p>
                        <h3 className="text-[22px] font-semibold text-[#1F2937]">{c.title}</h3>
                        <p className="text-[14px] text-[#5F6B76] mt-0.5">{c.institution}</p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </section>
        </Reveal>
      )}
    </div>
  );
}
